
"use client";

import { useState, useEffect, useRef, useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { X, Send, User as UserIcon, Bot, Users as FollowingIcon, MessageSquare as MessageSquareIcon, ChevronUp, ChevronDown, Loader2, ThumbsUp, ThumbsDown, Trash2, Paperclip, ExternalLink, Image as ImageIcon, Music2, FileText, Link2, Play, Disc, Mic, Minimize2, Square, Maximize2, Settings2, UsersRound, Radio, Headphones, Check, Plus, Phone, Video, PhoneMissed, Share2, Search, UserPlus, Pencil, Sparkles, Star, Heart, Crown, ShieldCheck, ShieldOff, UserMinus, Globe, Lock, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPublicName } from '@/lib/user-utils';
import { Avatar, AvatarFallback, AvatarImage, getInitials } from '../ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import { useMenuSide } from '@/contexts/user-layout-prefs-context';
import Link from 'next/link';
import { RichText } from '@/components/ui/rich-text';
import { useAuth } from '@/contexts/auth-context';
import { subscribeFollowing, subscribeFollowers, type FollowItem } from '@/services/follows';
import { addFavorite, removeFavorite, subscribeFavorites, type FavoriteItem } from '@/services/favorites';
import { getAllUsers, getBBCrewUsers, isBBCrewEmail, batchGetUserImages } from '@/services/admin';
import {
  getOrCreateDMRoom,
  createGroupRoom,
  addMembersToGroup,
  removeMemberFromGroup,
  setGroupAdmin,
  deleteGroupRoom,
  sendMessage,
  deleteMessage,
  subscribeMessages,
  subscribeChatRooms,
  setUserOnline,
  subscribePresence,
  markRoomRead,
  type ChatRoom,
  type ChatMessage,
} from '@/services/chat';
import { createParty, getActivePartyByChatId } from '@/services/listening-party';
import { createJamSession, joinJamSession, findActiveJamSessionForRoom, type JamSession } from '@/services/jam-sessions';
import { askSupportAI, logAIQuery, markQueryHelpful, type PlatformSearchResult } from '@/services/ai';
import { CallControls, IncomingCallBanner } from '@/components/chat/call-controls';
import { ChatSettingsPanel } from '@/components/chat/chat-settings-panel';
import { RecordMessage } from '@/components/chat/record-message';
import { sendMediaMessage, sendCallTrace, sendSharedContentMessage, editMessage, sendImageMessage, sendAvailabilitySlotsMessage, type SharedContent, type SharedAvailabilitySlot } from '@/services/chat';
import { getUpcomingAvailabilitySlots, type AvailabilitySlot } from '@/services/availability';
import { getMediaItems, getMediaItemsForUser, getReleases, getReleasesForUser } from '@/services/media';
import type { MediaItem, Release } from '@/types/media';
import { getUserPlaylists, getAllPlaylists, type Playlist } from '@/services/playlists';
import {
  subscribeIncomingCalls,
  answerCall,
  declineCall,
  type CallDoc,
} from '@/services/calls';
import { type RingtonePrefs } from '@/services/ringtone';
import { CreatorAdvisor } from '@/components/ai/creator-advisor';
import { storage } from '@/lib/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { useTranslations } from 'next-intl';

interface ChatWindowProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  initialRoomId?: string | null;
  initialTargetUserId?: string | null;
  onRoomOpened?: () => void;
  /** A Copilot prompt sent from outside (the `bb-copilot` event — coπ voice). */
  copilotRequest?: { prompt: string; id: number } | null;
  onCopilotRequestHandled?: () => void;
}

/**
 * Pages shareable in chat — every page in the standard nav EXCEPT admin-only ones.
 * /admin is intentionally excluded: it is a super-admin-only route and should
 * never be shared with regular group members who cannot access it.
 */
const SHAREABLE_PAGES: { href: string; label: string; navKey: string; emoji: string }[] = [
  { href: '/home',            label: 'Home',            navKey: 'home',          emoji: '🏠' },
  { href: '/dashboard',       label: 'Dashboard',       navKey: 'dashboard',     emoji: '📊' },
  { href: '/profile',         label: 'Profile',         navKey: 'profile',       emoji: '👤' },
  { href: '/marketplace',     label: 'Marketplace',     navKey: 'marketplace',   emoji: '🛒' },
  { href: '/jam-session',     label: 'Jam Session',     navKey: 'jamSession',    emoji: '🎧' },
  { href: '/party',           label: 'Listening Party', navKey: 'party',         emoji: '🎉' },
  { href: '/feed',            label: 'Feed',            navKey: 'feed',          emoji: '📋' },
  { href: '/events',          label: 'Events',          navKey: 'events',        emoji: '📅' },
  { href: '/news',            label: 'News',            navKey: 'news',          emoji: '📰' },
  { href: '/radio',           label: 'Radio',           navKey: 'radio',         emoji: '📻' },
  { href: '/wishlist',        label: 'Wishlist',        navKey: 'wishlist',      emoji: '⭐' },
  { href: '/synchronization', label: 'Sync Licensing',  navKey: 'sync',          emoji: '🎬' },
  { href: '/stats',           label: 'Stats',           navKey: 'stats',         emoji: '📈' },
  { href: '/music-coach',     label: 'Music Coach',     navKey: 'coach',         emoji: '🧠' },
  { href: '/media',           label: 'Media Vault',     navKey: 'media',         emoji: '🗄️' },
  { href: '/artwork-studio',  label: 'Artwork Studio',  navKey: 'artworkStudio', emoji: '🎨' },
  { href: '/film-maker',      label: 'Film Maker',      navKey: 'filmMaker',     emoji: '🎥' },
  { href: '/wallet',          label: 'Wallet',          navKey: 'wallet',        emoji: '💰' },
  { href: '/earnings',        label: 'Earnings',        navKey: 'earnings',      emoji: '💸' },
  { href: '/bookkeeping',     label: 'Bookkeeping',     navKey: 'bookkeeping',   emoji: '📚' },
  { href: '/dj',              label: 'DJ Mixer',        navKey: 'djMixer',       emoji: '🎚️' },
  { href: '/creator-ai',      label: 'Creator AI',      navKey: 'creatorAi',     emoji: '✨' },
  { href: '/settings',        label: 'Settings',        navKey: 'settings',      emoji: '⚙️' },
  // /admin is NOT included — admin panel is super-admin-only
];

// Desktop size presets
type ChatSize = 'quarter' | 'half' | 'full';

/**
 * GroupMosaicAvatar — fills a square with tiles of participant avatars.
 * Supports any count; tiles scale down as members increase (capped at 9 shown).
 */
function GroupMosaicAvatar({ room, liveImages = {}, size = 36 }: { room: ChatRoom; liveImages?: Record<string, string>; size?: number }) {
  const avatarMap: Record<string, string> = room.participantAvatars || {};
  const names: Record<string, string>     = room.participantNames    || {};
  const ids = room.participants || [];
  // Collect tiles (up to 9); prefer live-fetched image over stale participantAvatars
  const tiles = ids.slice(0, 9).map(id => ({
    id,
    url: liveImages[id] || avatarMap[id] || '',
    initial: (names[id] || id).charAt(0).toUpperCase(),
  }));
  const n = tiles.length;

  // Grid dimension: sqrt rounded up, max 3
  const cols = n <= 1 ? 1 : n <= 4 ? 2 : 3;
  const rows = n <= 1 ? 1 : n <= 2 ? 1 : n <= 4 ? 2 : 3;
  const cellW = Math.floor(size / cols);
  const cellH = Math.floor(size / rows);

  return (
    <div
      className="flex-shrink-0 overflow-hidden"
      style={{ width: size, height: size, display: 'flex', flexWrap: 'wrap' }}
    >
      {tiles.map((tile, i) => {
        // For odd counts, last tile may span full remaining width
        const isLastOdd = n % cols !== 0 && i === tiles.length - 1;
        const w = isLastOdd ? size : cellW;
        return (
          <div
            key={tile.id}
            style={{ width: w, height: cellH, flexShrink: 0, position: 'relative', overflow: 'hidden' }}
          >
            {tile.url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={tile.url} alt={tile.initial} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div
                style={{
                  width: '100%', height: '100%',
                  background: `hsl(${(tile.id.charCodeAt(0) * 37) % 360},40%,28%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: Math.max(7, Math.floor(cellW * 0.4)),
                  fontWeight: 700,
                  color: `hsl(${(tile.id.charCodeAt(0) * 37) % 360},70%,80%)`,
                  userSelect: 'none',
                }}
              >
                {tile.initial}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Support's first-encounter empty state — kept visually parallel to Copilot's
// (icon + one-line job description + starter chips) so the two read as two
// clearly different assistants rather than the same chat box twice: Support
// is "how do I use BB" + platform search, Copilot is career/industry advice.
const SUPPORT_STARTERS = [
  'How do I publish a track?',
  'Where do I set up payouts?',
  'Find tracks by [artist name]',
];

export function ChatWindow({ isOpen, setIsOpen, initialRoomId, initialTargetUserId, onRoomOpened, copilotRequest, onCopilotRequestHandled }: ChatWindowProps) {
  const t = useTranslations('chat');
  const tCommon = useTranslations('common');
  // Shareable pages by their menu names in this language ("Sync Licensing").
  const tNav = useTranslations('nav');
  const shareablePages = useMemo(
    () => SHAREABLE_PAGES.map(p => ({ ...p, label: (() => { try { return tNav(p.navKey as any); } catch { return p.label; } })() })),
    [tNav],
  );
  const tSchedule = useTranslations('schedule');
  const isMobile = useIsMobile();
  const { displayMode } = useSidebar();
  const menuSide = useMenuSide();
  const router = useRouter();
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (typeof window === 'undefined') return 'following';
    try { return localStorage.getItem('bb-chat-tab') || 'following'; } catch { return 'following'; }
  });
  const [isFollowingListCollapsed, setIsFollowingListCollapsed] = useState(true);
  const [chatSize, setChatSize] = useState<ChatSize>(() => {
    if (typeof window === 'undefined') return 'quarter';
    try { return (localStorage.getItem('bb-chat-size') as ChatSize) || 'quarter'; } catch { return 'quarter'; }
  });

  // Following state
  const [followingUsers, setFollowingUsers] = useState<FollowItem[]>([]);
  const [presenceMap, setPresenceMap] = useState<Record<string, boolean>>({});
  const [followingLoading, setFollowingLoading] = useState(true);

  // Chat state
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [showRecorder, setShowRecorder] = useState(false);
  const [sendingMedia, setSendingMedia] = useState(false);
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [ringtonePrefs, setRingtonePrefs] = useState<RingtonePrefs | undefined>(undefined);
  const [incomingCalls, setIncomingCalls] = useState<CallDoc[]>([]);

  // Group chat state
  const [groupSelectMode, setGroupSelectMode] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [groupNameDialogOpen, setGroupNameDialogOpen] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState('');
  const [groupCreating, setGroupCreating] = useState(false);
  const [partyLaunching, setPartyLaunching] = useState(false);
  const [jamLaunching, setJamLaunching] = useState(false);
  const [activeRoomJamSession, setActiveRoomJamSession] = useState<JamSession | null>(null);

  // Group info panel state
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [addMemberSelected, setAddMemberSelected] = useState<Set<string>>(new Set());
  const [addMemberSaving, setAddMemberSaving] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [togglingAdmin, setTogglingAdmin] = useState<string | null>(null);

  // Media share dialog state
  const [showMediaShare, setShowMediaShare] = useState(false);
  const [shareSearchQuery, setShareSearchQuery] = useState('');
  const [shareMediaItems, setShareMediaItems] = useState<MediaItem[]>([]);
  const [shareReleases, setShareReleases] = useState<Release[]>([]);
  const [sharePlaylists, setSharePlaylists] = useState<Playlist[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareTab, setShareTab] = useState<'tracks' | 'releases' | 'playlists'>('tracks');
  // Track which uid the share data was loaded for (so we reload on room/user change)
  const [shareLoadedForUid, setShareLoadedForUid] = useState<string | null>(null);

  // Page-link share dialog state
  const [showPageShare, setShowPageShare] = useState(false);
  const [pageSearchQuery, setPageSearchQuery] = useState('');

  // Availability-share dialog state — the sender's own upcoming open slots,
  // multi-select, sent as a single card (see sendAvailabilitySlotsMessage).
  const [showAvailabilityShare, setShowAvailabilityShare] = useState(false);
  const [availabilitySlotsToShare, setAvailabilitySlotsToShare] = useState<AvailabilitySlot[]>([]);
  const [availabilityShareLoading, setAvailabilityShareLoading] = useState(false);
  const [selectedAvailabilityIds, setSelectedAvailabilityIds] = useState<Set<string>>(new Set());
  const [sendingAvailability, setSendingAvailability] = useState(false);

  // Edit message state
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Chat image upload state
  const [uploadingChatImage, setUploadingChatImage] = useState(false);
  const chatImageInputRef = useRef<HTMLInputElement>(null);

  // Support AI state
  const [supportMessages, setSupportMessages] = useState<{ id: string; sender: 'user' | 'ai'; text: string; timestamp: string; logId?: string; results?: PlatformSearchResult[] }[]>([]);
  const [aiLoading, setAiLoading] = useState(false);

  // Pinned / starred contacts state
  const [pinnedFavs, setPinnedFavs] = useState<FavoriteItem[]>([]);
  const [favChatRooms, setFavChatRooms] = useState<FavoriteItem[]>([]);
  const [pinningUserId, setPinningUserId] = useState<string | null>(null);

  // Live user images — overrides stale avatars stored in follow/favorites docs
  const [liveContactImages, setLiveContactImages] = useState<Record<string, string>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const supportEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  // Admin and SuperAdmin both see all users in chat
  // Mirror useAdmin() logic: claims → email whitelist → roles / accountType
  const SUPER_ADMIN_EMAILS = ['breakinbreadswe@gmail.com'];
  const roles = (userProfile?.roles as string[] | undefined) ?? [];
  const isAdminOrSuperAdmin =
    SUPER_ADMIN_EMAILS.includes(user?.email ?? '') ||
    userProfile?.accountType === 'superadmin' ||
    userProfile?.accountType === 'admin' ||
    roles.some(r => ['superadmin', 'super admin', 'admin'].includes(r.toLowerCase()));

  // Load contacts: admins see all users; creators/consumers see followers + following
  useEffect(() => {
    if (!user?.uid) return;
    setFollowingLoading(true);

    if (isAdminOrSuperAdmin) {
      // Admin / SuperAdmin: load all platform users as contacts
      getAllUsers().then(users => {
        const items: FollowItem[] = users
          .filter(u => u.id !== user.uid)
          .map(u => ({
            id: u.id,
            userId: user.uid,
            targetId: u.id,
            targetName: u.displayName || u.email || 'User',
            targetImageUrl: u.profileImageUrl || u.photoURL || u.imageUrl || '',
            targetType: 'user' as const,
            createdAt: null as any,
          }));
        setFollowingUsers(items);
        setFollowingLoading(false);
      }).catch((err) => {
        console.error('Error loading all users:', err);
        setFollowingLoading(false);
      });
    } else {
      // Creators / Consumers: subscribe to BOTH following + followers in real-time
      // Merge and dedupe so the contact list mirrors Instagram/TikTok logic
      let following: FollowItem[] = [];
      let followers: FollowItem[] = [];
      let crewItems: FollowItem[] = [];
      let loaded = 0;

      // BB Crew members always see each other + Super Admin regardless of follow status
      const currentIsBBCrew = isBBCrewEmail(user.email);
      if (currentIsBBCrew) {
        getBBCrewUsers().then(crewUsers => {
          crewItems = crewUsers
            .filter(u => u.id !== user.uid)
            .map(u => ({
              id: `bbcrew_${u.id}`,
              userId: user.uid,
              targetId: u.id,
              targetName: u.displayName || u.email || 'Crew Member',
              targetImageUrl: u.profileImageUrl || u.photoURL || u.imageUrl || '',
              targetType: 'user' as const,
              createdAt: null as any,
            }));
          merge();
        }).catch(() => {});
      }

      const merge = () => {
        // Dedupe by targetId (people I follow) or userId (people who follow me → map to FollowItem shape)
        const seen = new Set<string>();
        const merged: FollowItem[] = [];

        // BB Crew pinned contacts first (always visible for crew members)
        for (const f of crewItems) {
          if (!seen.has(f.targetId)) {
            seen.add(f.targetId);
            merged.push(f);
          }
        }
        // People I follow
        for (const f of following) {
          if (!seen.has(f.targetId)) {
            seen.add(f.targetId);
            merged.push(f);
          }
        }
        // People who follow me (invert: show them as contacts using stored fromUser info)
        for (const f of followers) {
          if (!seen.has(f.userId)) {
            seen.add(f.userId);
            merged.push({
              id: `follower_${f.id}`,
              userId: user.uid,
              targetId: f.userId,
              // Use denormalised follower name/image stored at follow creation time
              targetName: (f as any).fromUserName || f.userId.slice(0, 8) + '…',
              targetImageUrl: (f as any).fromUserImageUrl || '',
              targetType: 'user' as const,
              createdAt: f.createdAt,
            });
          }
        }
        setFollowingUsers(merged);
        if (loaded === 2) setFollowingLoading(false);
      };

      const unsubFollowing = subscribeFollowing(user.uid, (items) => {
        following = items;
        loaded = Math.max(loaded, 1);
        merge();
      });

      const unsubFollowers = subscribeFollowers(user.uid, (items) => {
        followers = items;
        loaded = Math.max(loaded, 2);
        merge();
      });

      return () => { unsubFollowing(); unsubFollowers(); };
    }
  }, [user?.uid, user?.email, isAdminOrSuperAdmin]);

  // Batch-fetch live profile images whenever the contacts list changes
  // (follow/favorites docs store images at creation time → may be stale)
  useEffect(() => {
    const ids = followingUsers.map(f => f.targetId).filter(Boolean);
    if (!ids.length) return;
    let cancelled = false;
    batchGetUserImages(ids).then(map => {
      if (!cancelled) setLiveContactImages(prev => ({ ...prev, ...map }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [followingUsers]);

  // Set user online presence
  useEffect(() => {
    if (!user?.uid) return;
    setUserOnline(user.uid, true);
    const handleBeforeUnload = () => setUserOnline(user.uid, false);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      setUserOnline(user.uid, false);
    };
  }, [user?.uid]);

  // Subscribe to presence of following users
  useEffect(() => {
    const ids = followingUsers.map(f => f.targetId).filter(Boolean);
    if (ids.length === 0) return;
    const unsub = subscribePresence(ids, setPresenceMap);
    return unsub;
  }, [followingUsers]);

  // Subscribe to chat rooms
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeChatRooms(user.uid, setChatRooms);
    return unsub;
  }, [user?.uid]);

  // A `bb-copilot` request (coπ voice, app-shell) lands on the Copilot tab;
  // CreatorAdvisor picks the Copilot domain and sends the prompt.
  useEffect(() => {
    if (copilotRequest) setActiveTab('copilot');
  }, [copilotRequest]);

  // Persist chat tab + size to localStorage on every change
  useEffect(() => {
    try { localStorage.setItem('bb-chat-tab', activeTab); } catch { /* ignore */ }
  }, [activeTab]);
  useEffect(() => {
    try { localStorage.setItem('bb-chat-size', chatSize); } catch { /* ignore */ }
  }, [chatSize]);

  // Persist active room to sessionStorage (survives refresh, clears when browser closes)
  useEffect(() => {
    try {
      if (activeRoom?.id) sessionStorage.setItem('bb-chat-room-id', activeRoom.id);
      else sessionStorage.removeItem('bb-chat-room-id');
    } catch { /* ignore */ }
  }, [activeRoom?.id]);

  // After chat rooms load: if no explicit initialRoomId, restore the last active room
  const roomRestoredRef = useRef(false);
  useEffect(() => {
    if (roomRestoredRef.current || chatRooms.length === 0 || activeRoom || initialRoomId) return;
    try {
      const savedId = sessionStorage.getItem('bb-chat-room-id');
      if (!savedId) return;
      const room = chatRooms.find(r => r.id === savedId);
      if (room) {
        roomRestoredRef.current = true;
        setActiveRoom(room);
        setActiveTab('chat');
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRooms]);

  // Subscribe to incoming calls (real-time via Firestore)
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeIncomingCalls(user.uid, setIncomingCalls);
    return unsub;
  }, [user?.uid]);

  // Subscribe to pinned (starred) chat contacts + favorited group rooms
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeFavorites(user.uid, (favs) => {
      setPinnedFavs(favs.filter(f => f.itemType === 'user'));
      setFavChatRooms(favs.filter(f => f.itemType === 'chat'));
    });
    return unsub;
  }, [user?.uid]);

  // Toggle favorite for a group/artist chat room
  const handleToggleFavRoom = useCallback(async (room: ChatRoom) => {
    if (!user?.uid) return;
    const existing = favChatRooms.find(f => f.itemId === room.id);
    if (existing) {
      await removeFavorite(existing.id);
    } else {
      // Build a representative avatar — first participant that isn't the current user
      const otherId = room.participants.find(p => p !== user.uid);
      const avatar = otherId ? (room.participantAvatars?.[otherId] || '') : '';
      await addFavorite(user.uid, {
        itemId: room.id,
        itemType: 'chat',
        title: room.groupName || room.artistName || 'Group',
        subtitle: room.type === 'artist' ? 'Artist Room' : 'Group Chat',
        imageUrl: avatar,
        link: '',
      });
    }
  }, [user?.uid, favChatRooms]);

  // Auto-navigate to initialRoomId when chatRooms load
  useEffect(() => {
    if (!initialRoomId || chatRooms.length === 0) return;
    const room = chatRooms.find(r => r.id === initialRoomId);
    if (room) {
      setActiveRoom(room);
      setActiveTab('chat');
      onRoomOpened?.();
      // Focus message input after brief render delay
      setTimeout(() => messageInputRef.current?.focus(), 150);
    }
  }, [initialRoomId, chatRooms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open DM when initialTargetUserId is provided (from pinned header contact click)
  useEffect(() => {
    if (!initialTargetUserId || !user?.uid) return;
    (async () => {
      try {
        const room = await getOrCreateDMRoom(user.uid, userProfile?.displayName || '', userProfile?.profileImageUrl || '', initialTargetUserId, '', '');
        setActiveRoom(room);
        setActiveTab('chat');
        onRoomOpened?.();
        setTimeout(() => messageInputRef.current?.focus(), 150);
      } catch (err) {
        console.error('Failed to open DM from pinned contact:', err);
      }
    })();
  }, [initialTargetUserId, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to messages in active room + mark as read
  useEffect(() => {
    if (!activeRoom || !user?.uid) return;
    // Mark room as read when entering
    markRoomRead(activeRoom.id, user.uid);
    const unsub = subscribeMessages(activeRoom.id, (msgs) => {
      setChatMessages(msgs);
      // Mark read again whenever new messages arrive while viewing
      markRoomRead(activeRoom.id, user.uid);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    return unsub;
  }, [activeRoom?.id, user?.uid]);

  // Check if there's already an active jam session for this room (group/artist rooms only)
  useEffect(() => {
    if (!activeRoom?.id || (activeRoom.type !== 'group' && activeRoom.type !== 'artist')) {
      setActiveRoomJamSession(null);
      return;
    }
    let cancelled = false;
    findActiveJamSessionForRoom(activeRoom.id).then(s => {
      if (!cancelled) setActiveRoomJamSession(s);
    }).catch(() => {
      if (!cancelled) setActiveRoomJamSession(null);
    });
    return () => { cancelled = true; };
  }, [activeRoom?.id, activeRoom?.type]);

  // Auto-scroll support messages
  useEffect(() => {
    supportEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [supportMessages]);

  const handleStartChat = useCallback(async (targetId: string, targetName: string, targetAvatar: string) => {
    if (!user?.uid) return;
    try {
      const room = await getOrCreateDMRoom(
        user.uid,
        getPublicName(userProfile as any),
        userProfile?.profileImageUrl || '',
        targetId,
        targetName,
        targetAvatar
      );
      setActiveRoom(room);
      setActiveTab("chat");
    } catch (err) {
      console.error('Error starting chat:', err);
    }
  }, [user?.uid, userProfile]);

  // Incoming call handlers (must be after handleStartChat)
  const handleAnswerCall = useCallback(async (callId: string) => {
    try {
      await answerCall(callId);
      const call = incomingCalls.find(c => c.id === callId);
      if (call && user?.uid) {
        handleStartChat(call.callerId, call.callerName, call.callerAvatar || '');
      }
    } catch (err) {
      console.error('Failed to answer call:', err);
    }
  }, [incomingCalls, user?.uid, handleStartChat]);

  const handleDeclineCall = useCallback(async (callId: string) => {
    if (!user?.uid) return;
    try {
      const call = incomingCalls.find(c => c.id === callId);
      await declineCall(callId, user.uid);
      // Post trace — receiver declined
      if (call && activeRoom) {
        const traceRoomId = call.roomId || activeRoom.id;
        await sendCallTrace(
          traceRoomId,
          user.uid,
          userProfile?.displayName || user.displayName || 'User',
          userProfile?.profileImageUrl,
          { callType: call.type, callStatus: 'declined', callerId: call.callerId },
        ).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to decline call:', err);
    }
  }, [user?.uid, userProfile, incomingCalls, activeRoom]);

  const handleSendChatMessage = useCallback(async () => {
    if (!currentMessage.trim() || !activeRoom || !user?.uid) return;
    const text = currentMessage.trim();
    setCurrentMessage("");
    try {
      await sendMessage(
        activeRoom.id,
        user.uid,
        getPublicName(userProfile as any),
        text,
        userProfile?.profileImageUrl
      );
      // ── Fan-out PWA push to every other room participant ──
      // Best-effort, non-fatal. /api/push/send returns 200 even if web-push isn't
      // configured yet, so we just don't await + don't surface failures.
      const recipients = (activeRoom.participants || []).filter(p => p !== user.uid);
      if (recipients.length > 0) {
        try {
          const idToken = await user.getIdToken();
          const senderName = getPublicName(userProfile as any);
          const isGroup = activeRoom.type === 'group' || activeRoom.type === 'artist';
          const titlePrefix = isGroup ? `${activeRoom.groupName || activeRoom.artistName || 'Group'} · ` : '';
          await Promise.all(recipients.map(rid =>
            fetch('/api/push/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
              body: JSON.stringify({
                toUid: rid,
                payload: {
                  title: titlePrefix + senderName,
                  body: text.length > 140 ? text.slice(0, 137) + '…' : text,
                  tag: `chat-${activeRoom.id}`,
                  url: `/?room=${activeRoom.id}`,
                  data: { roomId: activeRoom.id, kind: 'chat-message' },
                },
              }),
            }).catch(() => null)
          ));
        } catch { /* push failures are non-fatal */ }
      }
    } catch (err) {
      console.error('Error sending message:', err);
    }
  }, [currentMessage, activeRoom, user?.uid, userProfile]);

  const handleSendSupportMessage = useCallback(async (textOverride?: string) => {
    const question = (textOverride ?? currentMessage).trim();
    if (!question || !user?.uid || aiLoading) return;
    setCurrentMessage("");

    const userMsg = {
      id: Date.now().toString(),
      sender: 'user' as const,
      text: question,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setSupportMessages(prev => [...prev, userMsg]);
    setAiLoading(true);

    try {
      const result = await askSupportAI(
        question,
        user.uid,
        userProfile?.displayName
      );
      setSupportMessages(prev => [...prev, {
        id: Date.now().toString() + 'r',
        sender: 'ai',
        text: result.answer,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        results: result.results.length > 0 ? result.results : undefined,
      }]);
    } catch (err) {
      setSupportMessages(prev => [...prev, {
        id: Date.now().toString() + 'r',
        sender: 'ai',
        text: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } finally {
      setAiLoading(false);
    }
  }, [currentMessage, user?.uid, userProfile, aiLoading]);

  const handleUnsendMessage = useCallback(async (messageId: string) => {
    if (!activeRoom || !user?.uid) return;
    try {
      await deleteMessage(messageId, activeRoom.id, user.uid);
    } catch (err) {
      console.error('Error unsending message:', err);
    }
  }, [activeRoom, user?.uid]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingMessageId || !user?.uid || !editingText.trim()) return;
    setEditSaving(true);
    try {
      await editMessage(editingMessageId, user.uid, editingText);
      setEditingMessageId(null);
      setEditingText("");
    } catch (err) {
      console.error('Error editing message:', err);
    } finally {
      setEditSaving(false);
    }
  }, [editingMessageId, user?.uid, editingText]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingText("");
  }, []);

  // ── Chat image upload ────────────────────────────────────────────────────────
  const handleChatImageFile = useCallback(async (file: File) => {
    if (!file || !user?.uid || !activeRoom) return;
    setUploadingChatImage(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `chatImages/${user.uid}/${Date.now()}.${ext}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      await sendImageMessage(
        activeRoom.id,
        user.uid,
        userProfile?.displayName || user.displayName || 'User',
        url,
        userProfile?.profileImageUrl,
      );
    } catch (e) {
      console.error('Chat image upload error:', e);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to send image. Please try again.' });
    } finally {
      setUploadingChatImage(false);
    }
  }, [user, userProfile, activeRoom, toast]);

  const handleSendMediaMessage = useCallback(async (media: {
    type: 'audio' | 'video';
    url: string;
    duration: number;
    mimeType: string;
  }) => {
    if (!activeRoom || !user?.uid) throw new Error('No active room or user');
    try {
      setSendingMedia(true);
      await sendMediaMessage(
        activeRoom.id,
        user.uid,
        getPublicName(userProfile as any),
        media.type,
        media.url,
        media.duration,
        media.mimeType,
        userProfile?.profileImageUrl
      );
      setShowRecorder(false);
    } catch (err) {
      console.error('Error sending media message:', err);
      throw err; // re-throw so RecordMessage can show the error
    } finally {
      setSendingMedia(false);
    }
  }, [activeRoom, user?.uid, userProfile]);

  const handleSendMessage = (tab: string) => {
    if (tab === 'chat') handleSendChatMessage();
    else if (tab === 'support') handleSendSupportMessage();
  };

  // ── Media share helpers ───────────────────────────────────────────────────
  const handleOpenMediaShare = useCallback(async () => {
    setShowMediaShare(true);
    setShareSearchQuery('');
    // Reload if we have no data OR if the uid changed since last load
    const needsLoad = shareMediaItems.length === 0 || shareLoadedForUid !== (user?.uid ?? null);
    if (needsLoad) {
      setShareLoading(true);
      try {
        if (isAdminOrSuperAdmin) {
          // Super/Admin sees ALL users' content
          const [items, releases, playlists] = await Promise.all([
            getMediaItems(),
            getReleases(),
            getAllPlaylists(),
          ]);
          setShareMediaItems(items);
          setShareReleases(releases);
          setSharePlaylists(playlists);
        } else {
          // Regular users see only their own content (including drafts/private)
          const uid = user?.uid ?? null;
          const [items, releases, playlists] = await Promise.all([
            uid ? getMediaItemsForUser(uid) : Promise.resolve([]),
            uid ? getReleasesForUser(uid) : Promise.resolve([]),
            uid ? getUserPlaylists(uid) : Promise.resolve([]),
          ]);
          setShareMediaItems(items);
          setShareReleases(releases);
          setSharePlaylists(playlists);
        }
        setShareLoadedForUid(user?.uid ?? null);
      } catch (err) {
        console.error('Error loading media for share:', err);
      } finally {
        setShareLoading(false);
      }
    }
  }, [shareMediaItems.length, shareLoadedForUid, user?.uid, isAdminOrSuperAdmin]);

  const handleSendSharedContent = useCallback(async (content: SharedContent) => {
    if (!activeRoom || !user?.uid) return;
    try {
      await sendSharedContentMessage(
        activeRoom.id,
        user.uid,
        getPublicName(userProfile as any),
        content,
        userProfile?.profileImageUrl
      );
      setShowMediaShare(false);
      setShowPageShare(false);
    } catch (err) {
      console.error('Error sharing content:', err);
    }
  }, [activeRoom, user?.uid, userProfile]);

  const handleOpenAvailabilityShare = useCallback(async () => {
    setShowAvailabilityShare(true);
    if (!user?.uid) return;
    setAvailabilityShareLoading(true);
    try {
      const slots = await getUpcomingAvailabilitySlots(user.uid);
      setAvailabilitySlotsToShare(slots);
      setSelectedAvailabilityIds(new Set(slots.map(s => s.id))); // default: share all upcoming
    } catch (err) {
      console.error('Error loading availability for share:', err);
    } finally {
      setAvailabilityShareLoading(false);
    }
  }, [user?.uid]);

  const handleSendAvailability = useCallback(async () => {
    if (!activeRoom || !user?.uid) return;
    const chosen: SharedAvailabilitySlot[] = availabilitySlotsToShare
      .filter(s => selectedAvailabilityIds.has(s.id))
      .map(s => ({ type: s.type, startAt: s.startAt, endAt: s.endAt, note: s.note }));
    if (chosen.length === 0) return;
    setSendingAvailability(true);
    try {
      await sendAvailabilitySlotsMessage(
        activeRoom.id,
        user.uid,
        getPublicName(userProfile as any),
        chosen,
        userProfile?.profileImageUrl
      );
      setShowAvailabilityShare(false);
    } catch (err) {
      console.error('Error sharing availability:', err);
    } finally {
      setSendingAvailability(false);
    }
  }, [activeRoom, user?.uid, userProfile, availabilitySlotsToShare, selectedAvailabilityIds]);

  // ── Group chat helpers ────────────────────────────────────────────────────
  const handleCreateGroup = useCallback(async () => {
    if (!user?.uid || selectedContactIds.size < 1 || !groupNameInput.trim()) return;
    try {
      setGroupCreating(true);
      const memberIds = [user.uid, ...Array.from(selectedContactIds)];
      const memberNames: Record<string, string> = {
        [user.uid]: getPublicName(userProfile as any),
      };
      const memberAvatars: Record<string, string> = {
        [user.uid]: userProfile?.profileImageUrl || '',
      };
      for (const id of selectedContactIds) {
        const contact = followingUsers.find(f => f.targetId === id);
        if (contact) {
          memberNames[id] = contact.targetName;
          memberAvatars[id] = contact.targetImageUrl || '';
        }
      }
      const room = await createGroupRoom(user.uid, memberIds, memberNames, memberAvatars, groupNameInput.trim());
      setGroupNameDialogOpen(false);
      setGroupNameInput('');
      setGroupSelectMode(false);
      setSelectedContactIds(new Set());
      setActiveRoom(room);
      setActiveTab('chat');
    } catch (err) {
      console.error('Error creating group:', err);
    } finally {
      setGroupCreating(false);
    }
  }, [user?.uid, userProfile, selectedContactIds, groupNameInput, followingUsers]);

  const handleStartPartyFromGroup = useCallback(async () => {
    if (!user?.uid || !activeRoom) return;
    try {
      setPartyLaunching(true);

      // Check if this group already has an active party — if so, rejoin it
      const existing = await getActivePartyByChatId(activeRoom.id);
      if (existing) {
        router.push(`/party/${existing.id}`);
        return;
      }

      // Create a new party scoped to this chat group
      const party = await createParty(
        user.uid,
        getPublicName(userProfile as any),
        activeRoom.groupName || 'Group Party',
        activeRoom.id,  // chatId — scopes this party to the group
      );
      const appOrigin = process.env.NEXT_PUBLIC_APP_URL || window.location.origin;
      const joinUrl = `${appOrigin}/party?code=${party.code}`;
      await sendMessage(
        activeRoom.id,
        user.uid,
        getPublicName(userProfile as any),
        `🎵 Listening Party started! Join with code **${party.code}** or visit: ${joinUrl}`,
        userProfile?.profileImageUrl
      );
      router.push(`/party/${party.id}`);
    } catch (err) {
      console.error('Error starting party:', err);
    } finally {
      setPartyLaunching(false);
    }
  }, [user?.uid, userProfile, activeRoom, router]);

  const handleStartJamFromGroup = useCallback(async () => {
    if (!user?.uid || !activeRoom) return;
    try {
      setJamLaunching(true);
      const displayName = getPublicName(userProfile as any);

      // ── Check for existing active session in this room ──────────────────
      // Only 1 jam session allowed per chat room at a time.
      const existing = activeRoomJamSession || await findActiveJamSessionForRoom(activeRoom.id);
      if (existing) {
        // Session already running — join it instead of creating a new one
        setActiveRoomJamSession(existing);
        await joinJamSession(existing.id, {
          id: user.uid,
          displayName,
          imageUrl: userProfile?.profileImageUrl || '',
          role: 'musician',
          isMuted: true,
        });
        router.push(`/jam-session?room=${existing.id}`);
        return;
      }

      // ── No existing session — create a new one ──────────────────────────
      const session = await createJamSession(
        user.uid,
        displayName,
        activeRoom.groupName || 'Group Jam',
        8,
        activeRoom.id, // link session to this chat room
      );
      setActiveRoomJamSession(session);
      // Host joins their own session immediately
      await joinJamSession(session.id, {
        id: user.uid,
        displayName,
        imageUrl: userProfile?.profileImageUrl || '',
        role: 'host',
        isMuted: true,
      });
      // Share code in chat so group members can join
      const appOrigin2 = process.env.NEXT_PUBLIC_APP_URL || window.location.origin;
      const joinUrl = `${appOrigin2}/jam-session?code=${session.code}`;
      await sendMessage(
        activeRoom.id,
        user.uid,
        displayName,
        `🎸 Jam Session started! Join with code **${session.code}** or click: ${joinUrl}`,
        userProfile?.profileImageUrl
      );
      // Navigate host directly into the room
      router.push(`/jam-session?room=${session.id}`);
    } catch (err) {
      console.error('Error starting jam:', err);
    } finally {
      setJamLaunching(false);
    }
  }, [user?.uid, userProfile, activeRoom, activeRoomJamSession, router]);

  const handleAddMembersToGroup = useCallback(async () => {
    if (!activeRoom || !user?.uid || addMemberSelected.size === 0) return;
    setAddMemberSaving(true);
    try {
      const newMembers = Array.from(addMemberSelected).map(id => {
        const contact = followingUsers.find(f => f.targetId === id);
        return { id, name: contact?.targetName || 'User', avatar: contact?.targetImageUrl || '' };
      });
      await addMembersToGroup(activeRoom.id, newMembers);
      const names = newMembers.map(m => m.name).join(', ');
      await sendMessage(
        activeRoom.id,
        user.uid,
        getPublicName(userProfile as any),
        `➕ Added ${names} to the group`,
        userProfile?.profileImageUrl
      );
      setAddMemberSelected(new Set());
      setAddingMembers(false);
    } catch (err) {
      console.error('Error adding members:', err);
    } finally {
      setAddMemberSaving(false);
    }
  }, [activeRoom, user?.uid, userProfile, addMemberSelected, followingUsers]);

  const handleDeleteGroup = useCallback(async () => {
    if (!activeRoom) return;
    setDeletingGroup(true);
    try {
      await deleteGroupRoom(activeRoom.id);
      setActiveRoom(null);
      setShowGroupInfo(false);
      setActiveTab('chat');
    } catch (err) {
      console.error('Error deleting group:', err);
    } finally {
      setDeletingGroup(false);
    }
  }, [activeRoom]);

  const handleRemoveMember = useCallback(async (memberId: string) => {
    if (!activeRoom || !user?.uid) return;
    if (!window.confirm(`Remove ${activeRoom.participantNames?.[memberId] || 'this member'} from the group?`)) return;
    setRemovingMember(memberId);
    try {
      await removeMemberFromGroup(activeRoom.id, memberId);
    } catch (err) {
      console.error('Error removing member:', err);
    } finally {
      setRemovingMember(null);
    }
  }, [activeRoom, user?.uid]);

  const handleToggleAdmin = useCallback(async (memberId: string, currentlyAdmin: boolean) => {
    if (!activeRoom || !user?.uid) return;
    setTogglingAdmin(memberId);
    try {
      await setGroupAdmin(activeRoom.id, memberId, !currentlyAdmin);
    } catch (err) {
      console.error('Error toggling admin:', err);
    } finally {
      setTogglingAdmin(null);
    }
  }, [activeRoom, user?.uid]);

  if (!isOpen) return null;

  const otherParticipantName = (room: ChatRoom) => {
    if (room.type === 'artist') return room.artistName || 'Artist Chat';
    if (room.type === 'group') return room.groupName || 'Group Chat';
    if (!user?.uid) return 'Unknown';
    const otherId = room.participants.find(p => p !== user.uid);
    return otherId ? (room.participantNames?.[otherId] || 'Unknown') : 'Unknown';
  };

  const otherParticipantAvatar = (room: ChatRoom) => {
    if (room.type === 'artist') return room.artistImageUrl || '';
    if (room.type === 'group') return '';  // groups use mosaic
    if (!user?.uid) return '';
    const otherId = room.participants.find(p => p !== user.uid);
    if (!otherId) return '';
    // Prefer live-fetched image → room participantAvatars → follow doc
    const live = liveContactImages[otherId];
    if (live) return live;
    const fromRoom = room.participantAvatars?.[otherId] || '';
    if (fromRoom) return fromRoom;
    const contact = followingUsers.find(fu => fu.targetId === otherId);
    return contact?.targetImageUrl || '';
  };

  const otherParticipantId = (room: ChatRoom) => {
    if (!user?.uid) return '';
    return room.participants.find(p => p !== user.uid) || '';
  };

  /** Close chat and navigate to a user's profile page */
  const goToProfile = (userId: string, slug?: string) => {
    setIsOpen(false);
    const href = slug ? `/artists/${slug}` : `/profile/${userId}`;
    router.push(href);
  };

  // Desktop size class lookup. The chat window now docks next to the sidebar on
  // the LEFT (Gordon: "change side on the chat pop up window... next to side menu
  // left aligned"), mirroring the play/toggle button which docks on the right.
  // Its left edge tracks the sidebar's current width (icon-only vs full) so it
  // never overlaps an expanded sidebar; quarter/half/full keep the same sizing,
  // just measured from the opposite edge.
  const isSidebarFull = !isMobile && displayMode === 'full';
  // Left/right-handed: the window docks beside the menu on whichever side it is.
  const menuOnRight = menuSide === 'right';
  const menuEdge = menuOnRight
    ? (isSidebarFull ? "right-[var(--sidebar-width,256px)]" : "right-[var(--sidebar-width-icon,50px)]")
    : (isSidebarFull ? "left-[var(--sidebar-width,256px)]" : "left-[var(--sidebar-width-icon,50px)]");
  const desktopSizeClass = {
    quarter: cn(
      menuEdge,
      "bottom-[var(--player-height,80px)] w-[calc((100%_-_var(--sidebar-width,256px))/2)] top-[calc(var(--header-height,50px)_+_(100vh_-_var(--header-height,50px)_-_var(--player-height,80px))/2)]"
    ),
    half: cn(
      menuEdge,
      "bottom-[var(--player-height,80px)] w-[calc((100%_-_var(--sidebar-width,256px))/2)] top-[var(--header-height,50px)]"
    ),
    full: cn(
      menuEdge,
      menuOnRight ? "left-[var(--sidebar-width-icon,50px)]" : "right-[var(--sidebar-width-icon,50px)]",
      "bottom-[var(--player-height,80px)] top-[var(--header-height,50px)]"
    ),
  }[chatSize];

  // ── Per-room unread helper ──────────────────────────────────────────────────
  // Returns true when the room has a message newer than the user's last-read time
  // and the last sender was someone else. We can't cheaply count exact unread
  // messages without querying every room's messages; a boolean flag is sufficient.
  const roomHasUnread = (room: ChatRoom): boolean => {
    if (!user?.uid) return false;
    if (!room.lastMessageAt) return false;
    if (room.lastSenderId === user.uid) return false;   // own message, nothing unread
    const lastRead = room.lastReadAt?.[user.uid];
    if (!lastRead) return true;                         // never opened = unread
    const lastReadMs: number = lastRead?.toMillis?.() ?? 0;
    const lastMsgMs: number  = room.lastMessageAt?.toMillis?.() ?? 0;
    return lastMsgMs > lastReadMs;
  };

  // Build a quick map: otherId → has unread DM, for highlighting contacts
  const contactUnreadMap = new Map<string, boolean>();
  for (const room of chatRooms) {
    if (room.type !== 'direct') continue;
    const otherId = room.participants.find(p => p !== user?.uid);
    if (otherId && roomHasUnread(room)) contactUnreadMap.set(otherId, true);
  }

  // All rooms with unread messages (used for tab badge + auto-navigation)
  const unreadRooms = chatRooms.filter(r => roomHasUnread(r));
  const unreadRoomCount = unreadRooms.length;

  // When chat opens: if there are unread rooms, jump straight to Chat tab.
  // If exactly one unread room, open it immediately so the user sees the message.
  const prevIsOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpen.current && unreadRooms.length > 0 && !initialRoomId && !copilotRequest) {
      setActiveTab('chat');
      if (unreadRooms.length === 1) {
        setActiveRoom(unreadRooms[0]);
      }
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card data-bg="chat" className={cn(
      "shadow-xl flex flex-col border bg-card text-card-foreground z-[55] overflow-hidden",
      isMobile
        // bottom-[56px] matches the mobile player bar's hardcoded h-[56px] exactly
        // (same convention as GlobalVuStrip's mobile strip and MobileFooterNav's tray)
        // — bottom-[76px] left a visible gap above the player bar.
        ? "fixed inset-x-0 bottom-[56px] top-[var(--header-height,50px)] max-w-full rounded-none border-t"
        : `fixed rounded-none ${desktopSizeClass}`
    )}>
      <CardHeader data-bg="chat-header" className="flex flex-row items-center justify-between p-3 border-b">
        <CardTitle className="text-lg font-semibold flex items-center">
          <MessageSquareIcon className="mr-2 h-5 w-5" />
          {t('title')}
        </CardTitle>
        <div className="flex items-center gap-0.5">
          {/* Size controls — desktop only */}
          {!isMobile && (
            <div className="flex items-center gap-0.5 mr-1" title="Resize chat window">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setChatSize('quarter')}
                className={cn("h-7 w-7", chatSize === 'quarter' && "bg-foreground text-background")}
                title={t('sizeQuarter')}
              >
                <Minimize2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setChatSize('half')}
                className={cn("h-7 w-7", chatSize === 'half' && "bg-foreground text-background")}
                title={t('sizeHalf')}
              >
                <Square className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setChatSize('full')}
                className={cn("h-7 w-7", chatSize === 'full' && "bg-foreground text-background")}
                title={t('sizeFull')}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="h-7 w-7" aria-label={t('close')}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </CardHeader>
      {/* Incoming call banners — visible on any tab */}
      {incomingCalls.map(call => (
        <IncomingCallBanner
          key={call.id}
          call={call}
          ringtonePrefs={ringtonePrefs}
          onAnswer={handleAnswerCall}
          onDecline={handleDeclineCall}
        />
      ))}

      <Tabs defaultValue="following" value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-grow min-h-0">

        {followingUsers.length > 0 && (
          <div className="px-2 py-1.5 border-b">
            <div
              className="flex gap-2 overflow-x-auto hide-scrollbar"
              style={{ scrollbarWidth: 'none' }}
            >
              {followingUsers.map(fu => {
                const hasUnread = contactUnreadMap.get(fu.targetId);
                const isOnline = presenceMap[fu.targetId];
                const isActive = !!activeRoom &&
                  activeRoom.type === 'direct' &&
                  otherParticipantId(activeRoom) === fu.targetId;
                return (
                  <button
                    key={fu.id}
                    onClick={() => handleStartChat(fu.targetId, fu.targetName, fu.targetImageUrl || '')}
                    title={fu.targetName}
                    className="relative shrink-0 rounded-none p-[3px] transition-all duration-150"
                    style={{
                      // ring-like outline drawn via box-shadow so it doesn't affect layout
                      boxShadow: hasUnread
                        ? '0 0 0 2px rgb(239 68 68)'           // red-500 — unread
                        : isActive
                        ? '0 0 0 2px hsl(var(--foreground)/0.7)' // subtle selected
                        : 'none',
                    }}
                  >
                    <Avatar className="h-9 w-9 rounded-none block">
                      <AvatarImage src={fu.targetImageUrl} />
                      <AvatarFallback className="text-xs rounded-none" name={fu.targetName}>{getInitials(fu.targetName)}</AvatarFallback>
                    </Avatar>
                    {/* Online dot — only when no unread */}
                    {isOnline && !hasUnread && (
                      <div className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-card" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <TabsList className="grid w-full grid-cols-5 rounded-none border-b h-9 shrink-0">
          <TabsTrigger value="following" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs gap-1 px-1">
            <FollowingIcon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">People</span>
          </TabsTrigger>
          <TabsTrigger value="groups" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs gap-1 px-1">
            <UsersRound className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Groups</span>
          </TabsTrigger>
          <TabsTrigger value="chat" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs gap-1 px-1 relative">
            <MessageSquareIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Chat</span>
            {unreadRoomCount > 0 && activeTab !== 'chat' && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 flex items-center justify-center text-[9px] font-bold rounded-full bg-destructive text-destructive-foreground leading-none">
                {unreadRoomCount > 9 ? '9+' : unreadRoomCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="support" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs gap-1 px-1">
            <Bot className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Support</span>
          </TabsTrigger>
          <TabsTrigger value="copilot" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary text-xs gap-1 px-1">
            <Sparkles className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Copilot</span>
          </TabsTrigger>
        </TabsList>

        <CardContent className="p-0 flex-grow min-h-0 flex flex-col">
          {/* FOLLOWING TAB */}
          <TabsContent value="following" className="flex-grow min-h-0 flex flex-col data-[state=inactive]:hidden mt-0">
            <ScrollArea className="flex-grow p-3">
              <div className="flex justify-between items-center pb-2">
                <Button
                  variant={groupSelectMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setGroupSelectMode(v => !v);
                    setSelectedContactIds(new Set());
                  }}
                  className="text-xs h-7 gap-1"
                >
                  <UsersRound className="h-3.5 w-3.5" />
                  {groupSelectMode ? 'Cancel' : 'New Group'}
                </Button>
              </div>

              {/* Selected members count + Create button */}
              {groupSelectMode && selectedContactIds.size >= 1 && (
                <div className="flex items-center justify-between mb-2 p-2 rounded-lg bg-primary/10 border border-primary/30">
                  <span className="text-xs font-medium">{selectedContactIds.size} selected</span>
                  <Button size="sm" className="text-xs h-7" onClick={() => { setGroupNameInput(''); setGroupNameDialogOpen(true); }}>
                    <Plus className="h-3 w-3 mr-1" />Create Group
                  </Button>
                </div>
              )}

              {followingLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : followingUsers.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">
                  {isAdminOrSuperAdmin ? "Loading all platform users..." : "Follow artists and users to see them here."}
                </p>
              ) : (
                followingUsers.map(fu => {
                  const isSelected = selectedContactIds.has(fu.targetId);
                  return (
                    <div
                      key={fu.id}
                      className={cn("flex items-center justify-between p-2 rounded-md hover:bg-secondary/50 transition-colors", groupSelectMode && "cursor-pointer", isSelected && "bg-primary/10")}
                      onClick={groupSelectMode ? () => {
                        setSelectedContactIds(prev => {
                          const next = new Set(prev);
                          if (next.has(fu.targetId)) next.delete(fu.targetId);
                          else next.add(fu.targetId);
                          return next;
                        });
                      } : undefined}
                    >
                      {(() => {
                        const contactAvatar = liveContactImages[fu.targetId] || fu.targetImageUrl || '';
                        return (
                          <div className="flex items-center gap-3">
                            {groupSelectMode && (
                              <div className={cn("w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors", isSelected ? "bg-primary border-primary" : "border-border")}>
                                {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                              </div>
                            )}
                            <button
                              onClick={groupSelectMode ? undefined : () => goToProfile(fu.targetId, fu.targetSlug)}
                              title={`View ${fu.targetName}'s profile`}
                              className={cn("hover:opacity-80 transition-opacity", groupSelectMode && "pointer-events-none")}
                            >
                              <div className="relative">
                                <Avatar className="h-9 w-9 rounded-none">
                                  <AvatarImage src={contactAvatar} />
                                  <AvatarFallback className="text-sm rounded-none" name={fu.targetName}>{getInitials(fu.targetName)}</AvatarFallback>
                                </Avatar>
                                {presenceMap[fu.targetId] && (
                                  <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-card" />
                                )}
                              </div>
                            </button>
                            <button
                              className={cn("text-left", !groupSelectMode && "hover:underline")}
                              onClick={groupSelectMode ? undefined : () => goToProfile(fu.targetId, fu.targetSlug)}
                              title={`View ${fu.targetName}'s profile`}
                            >
                              <p className="text-sm font-medium">{fu.targetName}</p>
                              <p className={cn("text-xs", presenceMap[fu.targetId] ? "text-green-500" : "text-muted-foreground")}>
                                {presenceMap[fu.targetId] ? t('online') : t('offline')}
                              </p>
                            </button>
                          </div>
                        );
                      })()}
                      {!groupSelectMode && (
                        <div className="flex items-center gap-2">
                          {contactUnreadMap.get(fu.targetId) && (
                            <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground flex-shrink-0">
                              •
                            </span>
                          )}
                          <Button variant="outline" size="sm" className="text-xs h-8" onClick={e => { e.stopPropagation(); handleStartChat(fu.targetId, fu.targetName, liveContactImages[fu.targetId] || fu.targetImageUrl || ''); }}>
                            Chat
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </ScrollArea>
          </TabsContent>

          {/* CHAT TAB */}
          {/* GROUPS TAB — filters chat rooms to type=group; entering one opens it in the Chat tab. */}
          <TabsContent value="groups" className="flex-grow min-h-0 flex flex-col data-[state=inactive]:hidden mt-0">
            <ScrollArea className="flex-grow p-3">
              <div className="flex justify-between items-center pb-2">
                <Button
                  variant={groupSelectMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setActiveTab('following');
                    setGroupSelectMode(true);
                    setSelectedContactIds(new Set());
                  }}
                  className="text-xs h-7 gap-1"
                >
                  <UsersRound className="h-3.5 w-3.5" />
                  New Group
                </Button>
              </div>
              {(() => {
                const groups = chatRooms.filter(r => r.type === 'group');
                if (groups.length === 0) {
                  return (
                    <div className="text-center py-10 px-3 space-y-2">
                      <UsersRound className="h-8 w-8 mx-auto text-muted-foreground/40" />
                      <p className="text-xs text-muted-foreground">No groups yet</p>
                      <p className="text-[11px] text-muted-foreground/80">
                        Tap <span className="font-semibold">New Group</span> and pick people on the People tab to start one.
                      </p>
                    </div>
                  );
                }
                return groups.map(room => (
                  <button
                    key={room.id}
                    onClick={() => { setActiveRoom(room); setActiveTab('chat'); }}
                    className={cn(
                      "flex items-center gap-3 w-full p-2 rounded-md hover:bg-secondary/50 text-left transition-colors",
                      roomHasUnread(room) && "bg-destructive/10 border-l-2 border-l-destructive pl-[6px]"
                    )}
                  >
                    <div className="flex-shrink-0 h-9 w-9 rounded-none overflow-hidden relative">
                      <GroupMosaicAvatar room={room} liveImages={liveContactImages} size={36} />
                      {/* visibility chip — bottom-right */}
                      <span title={room.visibility === 'public' ? 'Public group' : 'Private group'}
                            className={cn(
                              "absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full flex items-center justify-center border border-background",
                              room.visibility === 'public' ? "bg-emerald-600" : "bg-muted"
                            )}>
                        {room.visibility === 'public'
                          ? <Globe className="h-2 w-2 text-white" />
                          : <Lock className="h-2 w-2 text-foreground" />}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <p className={cn("text-sm truncate", roomHasUnread(room) ? "font-bold" : "font-medium")}>{room.groupName || 'Group Chat'}</p>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">· {room.participants.length}</span>
                        {roomHasUnread(room) && (
                          <span className="flex-shrink-0 ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground">•</span>
                        )}
                      </div>
                      <p className={cn("text-xs truncate", roomHasUnread(room) ? "text-foreground font-medium" : "text-muted-foreground")}>{room.lastMessage || 'No messages yet'}</p>
                    </div>
                  </button>
                ));
              })()}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="chat" className="flex-grow min-h-0 flex flex-col data-[state=inactive]:hidden mt-0">
            {!activeRoom ? (
              // Room list
              <ScrollArea className="flex-grow p-3">
                {chatRooms.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-8">{t('noConversations')}</p>
                ) : chatRooms.map(room => (
                  <div
                    key={room.id}
                    className={cn(
                      "flex items-center gap-3 w-full p-2 rounded-md hover:bg-secondary/50 text-left transition-colors",
                      roomHasUnread(room) && "bg-destructive/10 border-l-2 border-l-destructive pl-[6px]"
                    )}
                  >
                    {room.type === 'artist' ? (
                      /* Artist room — thumbnail with music note badge */
                      <button
                        onClick={() => room.artistId && goToProfile('', room.artistId)}
                        className="flex-shrink-0 relative hover:opacity-80 transition-opacity"
                        title={`View ${room.artistName} artist page`}
                      >
                        <Avatar className="h-9 w-9 rounded-none">
                          <AvatarImage src={room.artistImageUrl || ''} />
                          <AvatarFallback className="text-sm rounded-none" name={room.artistName || 'A'}>{getInitials(room.artistName || 'A')}</AvatarFallback>
                        </Avatar>
                        <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary flex items-center justify-center">
                          <Music2 className="h-2 w-2 text-primary-foreground" />
                        </div>
                      </button>
                    ) : room.type === 'group' ? (
                      <div className="flex-shrink-0 h-9 w-9 rounded-none overflow-hidden">
                        <GroupMosaicAvatar room={room} liveImages={liveContactImages} size={36} />
                      </div>
                    ) : (
                      <button
                        onClick={() => goToProfile(otherParticipantId(room))}
                        title={`View ${otherParticipantName(room)}'s profile`}
                        className="flex-shrink-0 hover:opacity-80 transition-opacity"
                      >
                        <Avatar className="h-9 w-9 rounded-none">
                          <AvatarImage src={otherParticipantAvatar(room)} />
                          <AvatarFallback className="text-sm rounded-none" name={otherParticipantName(room)}>{getInitials(otherParticipantName(room))}</AvatarFallback>
                        </Avatar>
                      </button>
                    )}
                    <button className="min-w-0 flex-1 text-left" onClick={() => setActiveRoom(room)}>
                      <div className="flex items-center gap-1">
                        <p className={cn("text-sm truncate", roomHasUnread(room) ? "font-bold" : "font-medium")}>{otherParticipantName(room)}</p>
                        {(room.type === 'group' || room.type === 'artist') && (
                          <span className="text-[10px] text-muted-foreground flex-shrink-0">· {room.participants.length}</span>
                        )}
                        {roomHasUnread(room) && (
                          <span className="flex-shrink-0 ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground">
                            •
                          </span>
                        )}
                      </div>
                      <p className={cn("text-xs truncate", roomHasUnread(room) ? "text-foreground font-medium" : "text-muted-foreground")}>{room.lastMessage || 'No messages yet'}</p>
                    </button>
                  </div>
                ))}
              </ScrollArea>
            ) : (
              // Active conversation
              <>
                <div className="flex items-center gap-1.5 p-2 border-b">
                  <Button variant="ghost" size="sm" className="h-7 text-xs flex-shrink-0" onClick={() => { setActiveRoom(null); setShowChatSettings(false); setShowGroupInfo(false); setAddingMembers(false); setAddMemberSelected(new Set()); }}>← Back</Button>
                  <button
                    className={cn("text-sm font-medium truncate transition-colors text-left flex-1 min-w-0", activeRoom.type === 'direct' && "hover:underline hover:text-primary")}
                    onClick={activeRoom.type === 'direct' ? () => goToProfile(otherParticipantId(activeRoom)) : undefined}
                    title={activeRoom.type === 'direct' ? "View profile" : undefined}
                  >
                    {otherParticipantName(activeRoom)}
                    {(activeRoom.type === 'group' || activeRoom.type === 'artist') && (
                      <span className="text-[10px] text-muted-foreground ml-1">· {activeRoom.participants.length}</span>
                    )}
                  </button>
                  {/* Listening Party + Jam Session + Share Media for group and artist chats */}
                  {(activeRoom.type === 'group' || activeRoom.type === 'artist') && (
                    <>
                      {/* Favourite button — pins group to header favourites bar */}
                      {(() => {
                        const isFav = favChatRooms.some(f => f.itemId === activeRoom.id);
                        return (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 flex-shrink-0"
                            onClick={() => handleToggleFavRoom(activeRoom)}
                            title={isFav ? t('removeFromFavorites') : t('addToFavorites')}
                          >
                            <Heart className={cn("h-3.5 w-3.5", isFav ? "fill-red-500 text-red-500" : "text-muted-foreground")} />
                          </Button>
                        );
                      })()}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px] px-1.5 flex-shrink-0 gap-1 bg-primary/10 border-primary/30 hover:bg-primary/20"
                        onClick={handleStartPartyFromGroup}
                        disabled={partyLaunching}
                        title={t('startListeningParty')}
                      >
                        {partyLaunching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Radio className="h-3 w-3" />}
                        <span className="hidden sm:inline">Party</span>
                      </Button>
                      {activeRoomJamSession ? (
                        /* Active jam session: show only Join button (no code badge) */
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[10px] px-1.5 flex-shrink-0 gap-1 bg-green-600 border-green-500 text-white hover:bg-green-500"
                          onClick={() => { router.push(`/jam-session?room=${activeRoomJamSession.id}`); setIsOpen(false); }}
                          title={`Join Jam Session: ${activeRoomJamSession.name}`}
                        >
                          <Headphones className="h-3 w-3" />
                          Join Jam
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[10px] px-1.5 flex-shrink-0 gap-1 bg-primary/10 border-primary/30 hover:bg-primary/20"
                          onClick={handleStartJamFromGroup}
                          disabled={jamLaunching}
                          title={t('startJamSession')}
                        >
                          {jamLaunching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Headphones className="h-3 w-3" />}
                          <span className="hidden sm:inline">Jam</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px] px-1.5 flex-shrink-0 gap-1 bg-primary/10 border-primary/30 hover:bg-primary/20"
                        onClick={handleOpenMediaShare}
                        title={t('shareTrack')}
                      >
                        <Share2 className="h-3 w-3" />
                        <span className="hidden sm:inline">Share</span>
                      </Button>
                    </>
                  )}
                  {/* Share Media for direct chats too */}
                  {activeRoom.type === 'direct' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 flex-shrink-0"
                      onClick={handleOpenMediaShare}
                      title={t('shareTrack')}
                    >
                      <Share2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {/* Group info button — only for group chats */}
                  {activeRoom.type === 'group' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn("h-7 w-7 p-0 flex-shrink-0", showGroupInfo && "bg-secondary")}
                      onClick={() => { setShowGroupInfo(prev => !prev); setAddingMembers(false); setAddMemberSelected(new Set()); }}
                      title={t('groupMembers')}
                    >
                      <UsersRound className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn("h-7 w-7 p-0 flex-shrink-0", showChatSettings && "bg-secondary")}
                    onClick={() => setShowChatSettings(prev => !prev)}
                    title={t('settings')}
                  >
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Chat settings panel (collapsible) */}
                {showChatSettings && (
                  <div className="border-b bg-background">
                    <ChatSettingsPanel onRingtonePrefsChange={setRingtonePrefs} />
                    {/* ── PWA push notifications opt-in ── one click subscribes
                        the browser; works on desktop Chrome/Edge/Firefox + Android
                        + iOS Safari 16.4+. Returns silently if not yet enabled
                        on the server (no VAPID keys). */}
                    <div className="px-3 pb-3">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const u = user;
                            if (!u) return;
                            const { isPushSupported, subscribeUserToPush } = await import('@/lib/push');
                            if (!isPushSupported()) {
                              toast({ variant: 'destructive', title: 'Not supported', description: "This browser doesn't support push notifications." });
                              return;
                            }
                            const idToken = await u.getIdToken();
                            const sub = await subscribeUserToPush(idToken);
                            if (sub) toast({ title: 'Notifications enabled', description: 'You\'ll get chat pings on this device.' });
                            else toast({ variant: 'destructive', title: 'Permission denied', description: 'Allow notifications in your browser settings.' });
                          } catch (err: any) {
                            toast({ variant: 'destructive', title: 'Could not enable', description: err?.message || 'Try again or check browser settings.' });
                          }
                        }}
                        className="w-full text-xs px-3 py-2 rounded-md border border-border bg-secondary/40 hover:bg-secondary text-left flex items-center justify-between gap-2"
                      >
                        <span>
                          <span className="font-semibold">Enable notifications</span>
                          <span className="block text-[10px] text-muted-foreground">Get a ping on this device when someone messages you.</span>
                        </span>
                        <span className="text-[10px] font-semibold text-primary">Turn on</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Group info panel — members list, add people, delete group */}
                {showGroupInfo && activeRoom.type === 'group' && (
                  <div className="border-b bg-background p-3 space-y-2 overflow-y-auto max-h-64">
                    {/* Privacy toggle — admin/creator only */}
                    {(activeRoom.createdBy === user?.uid || (activeRoom.adminIds?.includes(user?.uid ?? '') ?? false) || isAdminOrSuperAdmin) && (
                      <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-secondary/50 mb-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {(activeRoom.visibility === 'public') ? <Globe className="h-3.5 w-3.5 text-emerald-500" /> : <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold">{activeRoom.visibility === 'public' ? 'Public group' : 'Private group'}</p>
                            <p className="text-[10px] text-muted-foreground leading-tight">{activeRoom.visibility === 'public' ? 'Anyone on BB can find + join' : 'Invite-only · hidden from discovery'}</p>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            const next = activeRoom.visibility === 'public' ? 'private' : 'public';
                            try {
                              const { setGroupVisibility } = await import('@/services/chat');
                              await setGroupVisibility(activeRoom.id, next);
                            } catch (err) { console.error('toggle visibility', err); }
                          }}
                          className={cn(
                            "text-[10px] font-semibold px-2 py-1 rounded transition-colors flex-shrink-0",
                            activeRoom.visibility === 'public' ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-secondary border border-border hover:bg-muted"
                          )}
                        >
                          Make {activeRoom.visibility === 'public' ? 'Private' : 'Public'}
                        </button>
                      </div>
                    )}
                    {/* ── Share group link ── deep-links straight into this room
                        on any BB URL via ?room=<id>, auto-joining whoever
                        opens it (signing up/in first if they need to) as a
                        real participant — visibility only affects whether
                        the group is independently discoverable/searchable,
                        not whether this direct link works. */}
                    {activeRoom.type === 'group' && (
                      <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-secondary/50 mb-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Share2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold">Share group link</p>
                            <p className="text-[10px] text-muted-foreground leading-tight">
                              Whoever opens it joins the group — signs up or in first if they need to.
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            const link = `${window.location.origin}/?room=${encodeURIComponent(activeRoom.id)}`;
                            try {
                              if (navigator.share) {
                                await navigator.share({
                                  title: activeRoom.groupName || 'BB group chat',
                                  text: `Join "${activeRoom.groupName || 'this group'}" on Breakin' Bread`,
                                  url: link,
                                });
                              } else {
                                await navigator.clipboard.writeText(link);
                                toast({ title: 'Link copied', description: link });
                              }
                            } catch (err) {
                              // Fall back to clipboard if share API was cancelled
                              try {
                                await navigator.clipboard.writeText(link);
                                toast({ title: 'Link copied', description: link });
                              } catch { /* ignore */ }
                            }
                          }}
                          className="text-[10px] font-semibold px-2 py-1 rounded transition-colors flex-shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          Share
                        </button>
                      </div>
                    )}
                    {/* Member list */}
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                      Members · {activeRoom.participants.length}
                    </p>
                    <div className="space-y-1">
                      {activeRoom.participants.map(pid => {
                        const isCreator = pid === activeRoom.createdBy;
                        const isAdminMember = activeRoom.adminIds?.includes(pid) ?? false;
                        const canManageThisMember = (activeRoom.createdBy === user?.uid || (activeRoom.adminIds?.includes(user?.uid ?? '') ?? false) || isAdminOrSuperAdmin) && !isCreator && pid !== user?.uid;
                        return (
                          <div key={pid} className="flex items-center gap-2 py-0.5">
                            <Avatar className="h-6 w-6 rounded-none flex-shrink-0">
                              <AvatarImage src={activeRoom.participantAvatars?.[pid] || ''} />
                              <AvatarFallback className="text-[10px] rounded-none" name={activeRoom.participantNames?.[pid] || 'U'}>{getInitials(activeRoom.participantNames?.[pid] || 'U')}</AvatarFallback>
                            </Avatar>
                            <span className="text-xs flex-1 truncate">{activeRoom.participantNames?.[pid] || pid}</span>
                            {/* Role badges */}
                            {isCreator && (
                              <span title="Creator"><Crown className="h-3 w-3 text-amber-500 flex-shrink-0" /></span>
                            )}
                            {!isCreator && isAdminMember && (
                              <span title="Admin"><ShieldCheck className="h-3 w-3 text-blue-500 flex-shrink-0" /></span>
                            )}
                            {presenceMap[pid] && (
                              <div className="h-1.5 w-1.5 rounded-full bg-green-500 flex-shrink-0" />
                            )}
                            {/* Admin controls — only for creator/admins/superadmin */}
                            {canManageThisMember && (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                <button
                                  title={isAdminMember ? 'Remove admin' : 'Make admin'}
                                  disabled={togglingAdmin === pid}
                                  onClick={() => handleToggleAdmin(pid, isAdminMember)}
                                  className="p-0.5 rounded hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground disabled:opacity-50"
                                >
                                  {togglingAdmin === pid
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : isAdminMember
                                      ? <ShieldOff className="h-3 w-3" />
                                      : <ShieldCheck className="h-3 w-3" />
                                  }
                                </button>
                                <button
                                  title="Remove from group"
                                  disabled={removingMember === pid}
                                  onClick={() => handleRemoveMember(pid)}
                                  className="p-0.5 rounded hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive disabled:opacity-50"
                                >
                                  {removingMember === pid
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <UserMinus className="h-3 w-3" />
                                  }
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Add members section */}
                    {!addingMembers ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full h-7 text-xs gap-1"
                        onClick={() => setAddingMembers(true)}
                      >
                        <UserPlus className="h-3.5 w-3.5" /> Add People
                      </Button>
                    ) : (
                      <div className="space-y-1.5">
                        <p className="text-[11px] font-medium text-muted-foreground">Select contacts to add:</p>
                        <div className="space-y-0.5 max-h-24 overflow-y-auto">
                          {followingUsers
                            .filter(f => !activeRoom.participants.includes(f.targetId))
                            .map(f => {
                              const sel = addMemberSelected.has(f.targetId);
                              return (
                                <div
                                  key={f.id}
                                  className="flex items-center gap-2 p-1 rounded cursor-pointer hover:bg-secondary/50 transition-colors"
                                  onClick={() => setAddMemberSelected(prev => {
                                    const next = new Set(prev);
                                    if (next.has(f.targetId)) next.delete(f.targetId);
                                    else next.add(f.targetId);
                                    return next;
                                  })}
                                >
                                  <div className={cn("w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors", sel ? "bg-primary border-primary" : "border-border")}>
                                    {sel && <Check className="w-2 h-2 text-primary-foreground" />}
                                  </div>
                                  <Avatar className="h-5 w-5 rounded-none flex-shrink-0">
                                    <AvatarImage src={f.targetImageUrl} />
                                    <AvatarFallback className="text-[10px] rounded-none" name={f.targetName}>{getInitials(f.targetName)}</AvatarFallback>
                                  </Avatar>
                                  <span className="text-xs truncate">{f.targetName}</span>
                                </div>
                              );
                            })}
                          {followingUsers.filter(f => !activeRoom.participants.includes(f.targetId)).length === 0 && (
                            <p className="text-xs text-muted-foreground text-center py-2">All contacts already in group</p>
                          )}
                        </div>
                        <div className="flex gap-1.5">
                          <Button
                            variant="outline" size="sm"
                            className="flex-1 h-7 text-xs"
                            onClick={() => { setAddingMembers(false); setAddMemberSelected(new Set()); }}
                          >
                            {tCommon('cancel')}
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 h-7 text-xs"
                            disabled={addMemberSelected.size === 0 || addMemberSaving}
                            onClick={handleAddMembersToGroup}
                          >
                            {addMemberSaving
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : `Add${addMemberSelected.size > 0 ? ` (${addMemberSelected.size})` : ''}`
                            }
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Delete group — creator or admin only */}
                    {(activeRoom.createdBy === user?.uid || isAdminOrSuperAdmin) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full h-7 text-xs gap-1"
                        onClick={handleDeleteGroup}
                        disabled={deletingGroup}
                      >
                        {deletingGroup
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Trash2 className="h-3 w-3" />
                        }
                        {tCommon('delete')}
                      </Button>
                    )}
                  </div>
                )}

                {/* Call controls */}
                {activeRoom && user?.uid && (
                  <CallControls
                    currentUserId={user.uid}
                    currentUserName={getPublicName(userProfile as any)}
                    currentUserAvatar={userProfile?.profileImageUrl}
                    recipientId={otherParticipantId(activeRoom)}
                    recipientName={otherParticipantName(activeRoom)}
                    recipientAvatar={otherParticipantAvatar(activeRoom)}
                    roomId={activeRoom.id}
                    ringtonePrefs={ringtonePrefs}
                  />
                )}
                <ScrollArea data-bg="chat-body" className="flex-grow p-3">
                  {chatMessages.map(msg => {
                    const isOwn = msg.senderId === user?.uid;
                    const timestamp = msg.createdAt?.toDate
                      ? msg.createdAt.toDate().toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' · ' + msg.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : 'sending...';
                    // ── Call trace — centered event row ──────────────────────
                    if (msg.type === 'call' && msg.callTrace) {
                      const ct = msg.callTrace;
                      const isMissed = ct.callStatus === 'missed' || ct.callStatus === 'declined';
                      const CallIcon = isMissed ? PhoneMissed : (ct.callType === 'video' ? Video : Phone);
                      return (
                        <div key={msg.id} className="flex items-center justify-center my-2">
                          <div className={cn(
                            "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium",
                            isMissed
                              ? "bg-red-500/10 text-red-500 dark:text-red-400"
                              : "bg-green-500/10 text-green-700 dark:text-green-400"
                          )}>
                            <CallIcon className="h-3 w-3 flex-shrink-0" />
                            <span>{msg.text}</span>
                            <span className="text-[10px] opacity-60 ml-1">{timestamp.split(' · ')[1]}</span>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={msg.id} className={cn("flex items-end gap-2 mb-2 group w-full min-w-0", isOwn ? "flex-row-reverse" : "flex-row")}>
                        {!isOwn && (
                          <button
                            onClick={() => goToProfile(msg.senderId)}
                            title={`View ${msg.senderName}'s profile`}
                            className="flex-shrink-0 hover:opacity-80 transition-opacity"
                          >
                            <Avatar className="h-8 w-8 rounded-full">
                              <AvatarImage src={msg.senderAvatar} />
                              <AvatarFallback className="text-xs rounded-full" name={msg.senderName}>{getInitials(msg.senderName)}</AvatarFallback>
                            </Avatar>
                          </button>
                        )}
                        <div className={cn("flex items-center gap-1 min-w-0 flex-1", isOwn ? "flex-row-reverse" : "flex-row")}>
                          {isOwn && editingMessageId !== msg.id && (
                            // Bigger, outlined, and clearly spaced apart — small
                            // unbordered icons here made it too easy to hit Erase
                            // when aiming for Edit (and vice versa).
                            <div className={cn("flex gap-2", isOwn ? "flex-row-reverse" : "flex-row")}>
                              {/* Edit — only for plain text messages */}
                              {msg.type === 'text' && (
                                <button
                                  onClick={() => { setEditingMessageId(msg.id); setEditingText(msg.text); }}
                                  className="bb-hover-reveal transition-opacity h-7 w-7 flex items-center justify-center rounded-md border border-border bg-background hover:bg-primary/10 hover:border-primary text-muted-foreground hover:text-primary"
                                  title={t('editMessage')}
                                  aria-label={t('editMessage')}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => handleUnsendMessage(msg.id)}
                                className="bb-hover-reveal transition-opacity h-7 w-7 flex items-center justify-center rounded-md border border-border bg-background hover:bg-destructive/10 hover:border-destructive text-muted-foreground hover:text-destructive"
                                title={t('unsendMessage')}
                                aria-label={t('unsendMessage')}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                          {/* Inline edit mode */}
                          {editingMessageId === msg.id ? (
                            <div className="w-[min(280px,100%)] flex flex-col gap-1.5">
                              <textarea
                                className="w-full min-w-[180px] text-sm p-2 rounded-lg border border-primary bg-background text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                                rows={Math.min(6, editingText.split('\n').length + 1)}
                                value={editingText}
                                autoFocus
                                onChange={e => setEditingText(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                              />
                              <div className="flex gap-1 justify-end">
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-[11px] px-2 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  {tCommon('cancel')}
                                </button>
                                <button
                                  onClick={handleSaveEdit}
                                  disabled={editSaving || !editingText.trim()}
                                  className="text-[11px] px-2 py-0.5 rounded bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
                                >
                                  {editSaving ? tCommon('saving') : tCommon('save')}
                                </button>
                              </div>
                            </div>
                          ) : (
                          <div className={cn("max-w-[70%] p-2.5 rounded-lg shadow-sm text-sm",
                            isOwn ? "bg-primary text-primary-foreground rounded-br-none" : "bg-secondary text-secondary-foreground rounded-bl-none"
                          )}>
                            {msg.type === 'image' && msg.mediaData?.url ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={msg.mediaData.url}
                                alt="Image"
                                className="max-w-[220px] max-h-[280px] rounded-lg object-cover cursor-pointer"
                                onClick={() => window.open(msg.mediaData!.url, '_blank')}
                              />
                            ) : msg.type === 'audio' || msg.type === 'video' ? (
                              <div className="space-y-2">
                                <p className="text-xs font-medium">{msg.text}</p>
                                {msg.mediaData && (
                                  msg.type === 'audio' ? (
                                    <audio
                                      src={msg.mediaData.url}
                                      controls
                                      className="w-full h-8 rounded bg-black/20"
                                    />
                                  ) : (
                                    <video
                                      src={msg.mediaData.url}
                                      controls
                                      className="w-full rounded bg-black max-h-48"
                                    />
                                  )
                                )}
                              </div>
                            ) : msg.type === 'shared_content' && msg.sharedContent?.contentType === 'page' ? (
                              /* ── Page link — horizontal compact card ── */
                              <Link href={msg.sharedContent.link} className="block no-underline" onClick={() => setIsOpen(false)}>
                                <div className={cn("rounded-lg border p-2.5 flex items-center gap-2.5 max-w-[200px] transition-opacity hover:opacity-80", isOwn ? "border-primary-foreground/20 bg-primary-foreground/5" : "border-border bg-secondary/30")}>
                                  <div className="flex-shrink-0 h-9 w-9 rounded-md bg-muted flex items-center justify-center text-lg leading-none">
                                    {SHAREABLE_PAGES.find(p => p.href === msg.sharedContent!.link)?.emoji ?? '🔗'}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-[10px] uppercase opacity-50 font-medium leading-none mb-0.5">Page</p>
                                    <p className="text-xs font-semibold truncate">{msg.sharedContent.title}</p>
                                  </div>
                                  <ExternalLink className="h-3 w-3 opacity-40 flex-shrink-0" />
                                </div>
                              </Link>
                            ) : msg.type === 'shared_content' && msg.sharedContent ? (
                              /* ── Media / content card — square image + text ── */
                              // Card and thumbnail now share ONE width (w-full max-w-[220px]
                              // on both) — previously the thumbnail alone was capped at
                              // max-w-[160px] while the card inherited the much wider message
                              // bubble's own max-w-[70%], leaving a big empty gap to the right
                              // of the image. Also a bit bigger than the old 160px cap.
                              <Link href={msg.sharedContent.link} className="block no-underline w-full max-w-[220px]" onClick={() => setIsOpen(false)}>
                                <div className={cn("rounded-lg overflow-hidden border transition-opacity hover:opacity-90", isOwn ? "border-primary-foreground/20" : "border-border")}>
                                  {/* Thumbnail with graceful fallback if image fails to load */}
                                  {(() => {
                                    const ct = msg.sharedContent!.contentType;
                                    const FallbackIcon = ct === 'release' ? Disc : ct === 'artist' ? FollowingIcon : Music2;
                                    return (
                                      <div className="relative aspect-square w-full bg-muted overflow-hidden flex items-center justify-center">
                                        {/* Fallback icon — hidden by default, shown when img errors */}
                                        <FallbackIcon className="h-8 w-8 text-muted-foreground/40" id={`fb-${msg.id}`} style={{ display: msg.sharedContent!.imageUrl ? 'none' : 'block' }} />
                                        {msg.sharedContent!.imageUrl && (
                                          // eslint-disable-next-line @next/next/no-img-element
                                          <img
                                            src={msg.sharedContent!.imageUrl}
                                            alt={msg.sharedContent!.title}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                              const fb = document.getElementById(`fb-${msg.id}`);
                                              if (fb) fb.style.display = 'block';
                                            }}
                                          />
                                        )}
                                      </div>
                                    );
                                  })()}
                                  <div className="p-2">
                                    <div className="flex items-center gap-1 mb-0.5">
                                      {msg.sharedContent.contentType === 'release' ? <Disc className="h-3 w-3 flex-shrink-0 opacity-70" /> : msg.sharedContent.contentType === 'artist' ? <FollowingIcon className="h-3 w-3 flex-shrink-0 opacity-70" /> : <Music2 className="h-3 w-3 flex-shrink-0 opacity-70" />}
                                      <span className="text-[10px] uppercase opacity-60 font-medium">{msg.sharedContent.contentType}</span>
                                    </div>
                                    <p className="text-xs font-semibold leading-tight line-clamp-2">{msg.sharedContent.title}</p>
                                    {msg.sharedContent.subtitle && (
                                      <p className="text-[10px] opacity-70 mt-0.5 truncate">{msg.sharedContent.subtitle}</p>
                                    )}
                                  </div>
                                </div>
                              </Link>
                            ) : msg.type === 'availability_slots' && msg.availabilitySlots?.length ? (
                              /* ── Shared availability card — a snapshot list of the
                                  sender's own open slots at share-time, not a live
                                  booking. Links to /schedule so recipients can add
                                  their own. */
                              <div className={cn("w-full max-w-[240px] rounded-lg overflow-hidden border", isOwn ? "border-primary-foreground/20" : "border-border")}>
                                <div className={cn("flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase font-medium", isOwn ? "bg-primary-foreground/10" : "bg-muted/60")}>
                                  <CalendarClock className="h-3 w-3 opacity-70" />
                                  {t('availabilityCardTitle')}
                                </div>
                                <div className="p-2 space-y-1.5">
                                  {msg.availabilitySlots.map((s, i) => (
                                    <div key={i} className="text-xs">
                                      <span className="opacity-60 uppercase text-[10px] mr-1">{tSchedule(`type_${s.type}`)}</span>
                                      <span className="font-medium">
                                        {new Date(s.startAt).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                        {' → '}
                                        {new Date(s.endAt).toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                      </span>
                                      {s.note && <div className="opacity-70 truncate">{s.note}</div>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <RichText text={msg.text} className="text-sm" />
                            )}
                            <p className={cn("text-[10px] mt-1", isOwn ? "text-primary-foreground/60" : "text-muted-foreground/70")}>
                              {timestamp}{msg.edited && <span className="opacity-60 ml-1">(edited)</span>}
                            </p>
                          </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </ScrollArea>
                {/* Show recorder or text input */}
                {showRecorder && activeRoom ? (
                  <RecordMessage
                    roomId={activeRoom.id}
                    userId={user?.uid ?? ''}
                    onMessageSent={handleSendMediaMessage}
                    onCancel={() => setShowRecorder(false)}
                  />
                ) : (
                  <div data-bg="chat-button" className="p-3 border-t flex items-center gap-2 flex-shrink-0">
                    {/* Hidden image file input */}
                    <input
                      ref={chatImageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) await handleChatImageFile(file);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0 hover:bg-primary/20"
                      onClick={() => setShowRecorder(true)}
                      title={t('recordMedia')}
                      disabled={sendingMedia || uploadingChatImage}
                    >
                      <Mic className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0 hover:bg-primary/20"
                      onClick={() => chatImageInputRef.current?.click()}
                      title={t('sendImage')}
                      disabled={sendingMedia || uploadingChatImage}
                    >
                      {uploadingChatImage
                        ? <Loader2 className="h-5 w-5 animate-spin" />
                        : <ImageIcon className="h-5 w-5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0 hover:bg-primary/20"
                      onClick={() => { setShowPageShare(true); setPageSearchQuery(''); }}
                      title={t('shareLink')}
                      disabled={sendingMedia || uploadingChatImage}
                    >
                      <ExternalLink className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0 hover:bg-primary/20"
                      onClick={handleOpenAvailabilityShare}
                      title={t('shareAvailability')}
                      disabled={sendingMedia || uploadingChatImage}
                    >
                      <CalendarClock className="h-5 w-5" />
                    </Button>
                    <textarea
                      ref={messageInputRef}
                      placeholder={t('typeMessage')}
                      rows={1}
                      className="flex-1 min-w-0 text-sm bg-background border border-input rounded-md px-3 py-2 resize-none overflow-y-auto leading-snug focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground disabled:opacity-50"
                      style={{ minHeight: '40px', maxHeight: '120px' }}
                      value={currentMessage}
                      onChange={(e) => {
                        setCurrentMessage(e.target.value);
                        // Auto-resize
                        const el = e.target;
                        el.style.height = 'auto';
                        el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage('chat');
                          // Reset height after send
                          setTimeout(() => {
                            if (messageInputRef.current) messageInputRef.current.style.height = '40px';
                          }, 0);
                        }
                      }}
                      disabled={sendingMedia || uploadingChatImage}
                    />
                    <Button size="icon" onClick={() => handleSendMessage('chat')} className="h-10 w-10 flex-shrink-0" disabled={!currentMessage.trim() || sendingMedia || uploadingChatImage}>
                      <Send className="h-5 w-5" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* SUPPORT AI TAB */}
          <TabsContent value="support" className="flex-grow min-h-0 flex flex-col data-[state=inactive]:hidden mt-0">
            {/* Persistent header — same "signage" position as Copilot's domain
                strip, so switching tabs makes the different job obvious before
                you type anything. */}
            <div className="px-3 pt-3 pb-2 border-b border-border/60">
              <div className="flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Platform Help</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">Ask how to use Breakin&apos; Bread, or search for an artist, track, or release.</p>
            </div>
            <ScrollArea className="flex-grow p-3">
              {supportMessages.length === 0 && !aiLoading && (
                <div className="space-y-1.5 py-1">
                  <p className="text-xs font-medium text-muted-foreground">Try asking:</p>
                  {SUPPORT_STARTERS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSendSupportMessage(q)}
                      className="w-full text-left text-xs p-2 rounded bg-muted/60 hover:bg-muted text-foreground/80 hover:text-foreground transition-colors border border-border/40 hover:border-border"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {supportMessages.map(msg => (
                <div key={msg.id} className={cn("flex flex-col gap-1 mb-3", msg.sender === 'user' ? "items-end" : "items-start")}>
                  <div className={cn("flex items-end gap-2", msg.sender === 'user' ? "justify-end" : "justify-start")}>
                    {msg.sender !== 'user' && (
                      <Avatar className="h-8 w-8 rounded-full self-start flex-shrink-0">
                        <AvatarFallback className="text-xs rounded-full bg-primary text-primary-foreground">AI</AvatarFallback>
                      </Avatar>
                    )}
                    <div className={cn("max-w-[70%] p-2.5 rounded-lg shadow-sm text-sm",
                      msg.sender === 'user' ? "bg-primary text-primary-foreground rounded-br-none" : "bg-secondary text-secondary-foreground rounded-bl-none"
                    )}>
                      <p className="whitespace-pre-line text-sm leading-snug">{msg.text}</p>
                      <p className={cn("text-[10px] mt-1", msg.sender === 'user' ? "text-primary-foreground/70" : "text-muted-foreground/80")}>{msg.timestamp}</p>
                    </div>
                    {msg.sender === 'user' && (
                      <Avatar className="h-8 w-8 rounded-full self-start flex-shrink-0">
                        <AvatarImage src={userProfile?.profileImageUrl} />
                        <AvatarFallback className="text-xs rounded-full">{getInitials(getPublicName(userProfile as any)) || 'ME'}</AvatarFallback>
                      </Avatar>
                    )}
                  </div>

                  {/* Platform result cards (only for AI messages with results) */}
                  {msg.sender === 'ai' && msg.results && msg.results.length > 0 && (
                    <div className="ml-10 space-y-1 w-full max-w-[85%]">
                      {msg.results.map((res, i) => (
                        <Link
                          key={`${res.id}-${i}`}
                          href={res.link}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-2 p-2 rounded-md bg-background border border-border/60 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
                        >
                          {res.imageUrl ? (
                            <div className="w-9 h-9 rounded flex-shrink-0 overflow-hidden bg-muted">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={res.imageUrl} alt={res.title} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-9 h-9 rounded flex-shrink-0 bg-muted flex items-center justify-center">
                              {res.type === 'artist' ? <FollowingIcon className="h-4 w-4 text-muted-foreground" /> :
                               res.type === 'release' ? <Disc className="h-4 w-4 text-muted-foreground" /> :
                               <Music2 className="h-4 w-4 text-muted-foreground" />}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium truncate group-hover:text-primary transition-colors">{res.title}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{res.subtitle || res.type}</p>
                          </div>
                          <ExternalLink className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {aiLoading && (
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="h-8 w-8 rounded-full">
                    <AvatarFallback className="text-xs rounded-full bg-primary text-primary-foreground">AI</AvatarFallback>
                  </Avatar>
                  <div className="bg-secondary p-2.5 rounded-lg rounded-bl-none">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={supportEndRef} />
            </ScrollArea>
            <div className="p-3 border-t flex items-center gap-2 flex-shrink-0">
              <textarea
                placeholder={t('askAdvisor')}
                rows={1}
                className="flex-1 min-w-0 text-sm bg-background border border-input rounded-md px-3 py-2 resize-none overflow-y-auto leading-snug focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground disabled:opacity-50"
                style={{ minHeight: '40px', maxHeight: '120px' }}
                value={activeTab === 'support' ? currentMessage : ''}
                onChange={(e) => {
                  setCurrentMessage(e.target.value);
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage('support');
                    setTimeout(() => { e.currentTarget.style.height = '40px'; }, 0);
                  }
                }}
                disabled={aiLoading}
              />
              <Button size="icon" onClick={() => handleSendMessage('support')} className="h-10 w-10 flex-shrink-0" disabled={!currentMessage.trim() || aiLoading}>
                <Send className="h-5 w-5" />
              </Button>
            </div>
          </TabsContent>

          {/* BB Copilot — same CreatorAdvisor component /creator-ai uses, embedded here
              so it's reachable without leaving whatever page you're on. */}
          <TabsContent value="copilot" className="flex-grow min-h-0 flex flex-col data-[state=inactive]:hidden mt-0">
            <CreatorAdvisor
              variant="compact"
              className="flex-1 min-h-0"
              request={copilotRequest}
              onRequestHandled={onCopilotRequestHandled}
            />
          </TabsContent>
        </CardContent>
      </Tabs>

      {/* Media Share Dialog */}
      <Dialog open={showMediaShare} onOpenChange={setShowMediaShare}>
        <DialogContent className="max-w-sm z-[110]" overlayClassName="z-[110]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-4 w-4" />
              Share Media
            </DialogTitle>
          </DialogHeader>
          {/* Tab toggle */}
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            <button
              onClick={() => { setShareTab('tracks'); setShareSearchQuery(''); }}
              className={cn("flex-1 text-xs py-1.5 rounded-md font-medium transition-colors",
                shareTab === 'tracks' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              Tracks
            </button>
            <button
              onClick={() => { setShareTab('releases'); setShareSearchQuery(''); }}
              className={cn("flex-1 text-xs py-1.5 rounded-md font-medium transition-colors",
                shareTab === 'releases' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              Releases
            </button>
            <button
              onClick={() => { setShareTab('playlists'); setShareSearchQuery(''); }}
              className={cn("flex-1 text-xs py-1.5 rounded-md font-medium transition-colors",
                shareTab === 'playlists' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              Playlists
            </button>
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input type="search"
              placeholder={shareTab === 'tracks' ? 'Search tracks…' : shareTab === 'releases' ? 'Search releases…' : 'Search playlists…'}
              value={shareSearchQuery}
              onChange={e => setShareSearchQuery(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          {/* Results */}
          <ScrollArea className="h-64">
            {shareLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : shareTab === 'tracks' ? (
              <div className="space-y-1 pr-2">
                {shareMediaItems
                  .filter(item => {
                    if (!shareSearchQuery) return true;
                    const q = shareSearchQuery.toLowerCase();
                    // item.artist is a string on some works and an {name} object on
                    // others (EntityRef-shaped) — casting it straight to `string`
                    // was a lie at runtime: calling .toLowerCase() on the object
                    // form threw "...toLowerCase is not a function" and crashed
                    // this whole dialog ("error when adding a track to chat").
                    const artistName = typeof item.artist === 'string' ? item.artist : (item.artist as any)?.name || '';
                    return item.title?.toLowerCase().includes(q) || artistName.toLowerCase().includes(q);
                  })
                  .slice(0, 50)
                  .map(item => (
                    <button
                      key={item.workId}
                      onClick={() => handleSendSharedContent({
                        contentType: 'track',
                        contentId: item.workId,
                        title: item.title || 'Untitled',
                        subtitle: typeof item.artist === 'string' ? item.artist : (item.artist as any)?.name || '',
                        imageUrl: item.coverArtUrl || item.imageUrl || '',
                        link: `/music/${item.workId}`,
                        audioUrl: item.mp3Url || item.audioUrl || '',
                      })}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div className="h-10 w-10 rounded flex-shrink-0 bg-muted overflow-hidden">
                        {(item.coverArtUrl || item.imageUrl) ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={item.coverArtUrl || item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{item.title || 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {typeof item.artist === 'string' ? item.artist : (item.artist as any)?.name || 'Unknown artist'}
                          {item.status && item.status !== 'Published' && (
                            <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium">{item.status}</span>
                          )}
                        </p>
                      </div>
                      <Share2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    </button>
                  ))}
                {shareMediaItems.filter(item => {
                  if (!shareSearchQuery) return true;
                  const q = shareSearchQuery.toLowerCase();
                  const artistName = typeof item.artist === 'string' ? item.artist : (item.artist as any)?.name || '';
                  return item.title?.toLowerCase().includes(q) || artistName.toLowerCase().includes(q);
                }).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No tracks found</p>
                )}
              </div>
            ) : shareTab === 'releases' ? (
              <div className="space-y-1 pr-2">
                {shareReleases
                  .filter(r => !shareSearchQuery || (r.title || '').toLowerCase().includes(shareSearchQuery.toLowerCase()))
                  .slice(0, 50)
                  .map(r => {
                    const ra = r as any;
                    const rTitle = r.title || ra.name || 'Untitled';
                    const rArtist = ra.artistName || (typeof ra.artist === 'string' ? ra.artist : ra.artist?.name) || '';
                    const rCover = r.coverArtUrl || ra.images?.[0] || '';
                    const rId = r.releaseId || ra.id;
                    const rType = ra.releaseType || '';
                    return (
                      <button
                        key={rId}
                        onClick={() => handleSendSharedContent({
                          contentType: 'release',
                          contentId: rId,
                          title: rTitle,
                          subtitle: rArtist,
                          imageUrl: rCover,
                          link: `/releases/${rId}`,
                        })}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                      >
                        <div className="h-10 w-10 rounded flex-shrink-0 bg-muted overflow-hidden">
                          {rCover ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={rCover} alt={rTitle} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Disc className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{rTitle}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {rArtist || 'Unknown artist'}
                            {rType && <span className="ml-1.5 opacity-50 capitalize">{rType}</span>}
                          </p>
                        </div>
                        <Share2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      </button>
                    );
                  })}
                {shareReleases.filter(r => !shareSearchQuery || (r.title || '').toLowerCase().includes(shareSearchQuery.toLowerCase())).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No releases found</p>
                )}
              </div>
            ) : (
              /* Playlists tab */
              <div className="space-y-1 pr-2">
                {sharePlaylists
                  .filter(p => !shareSearchQuery || p.title.toLowerCase().includes(shareSearchQuery.toLowerCase()))
                  .slice(0, 50)
                  .map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleSendSharedContent({
                        contentType: 'playlist',
                        contentId: p.id,
                        title: p.title,
                        subtitle: p.creatorName || '',
                        imageUrl: p.coverImageUrl || '',
                        link: `/playlists/${p.id}`,
                      })}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div className="h-10 w-10 rounded flex-shrink-0 bg-muted overflow-hidden">
                        {p.coverImageUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={p.coverImageUrl} alt={p.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{p.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {p.trackCount} tracks
                          {p.visibility !== 'public' && (
                            <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-muted font-medium capitalize">{p.visibility}</span>
                          )}
                        </p>
                      </div>
                      <Share2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    </button>
                  ))}
                {sharePlaylists.filter(p => !shareSearchQuery || p.title.toLowerCase().includes(shareSearchQuery.toLowerCase())).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No playlists found</p>
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Page Link Share Dialog */}
      <Dialog open={showPageShare} onOpenChange={setShowPageShare}>
        <DialogContent className="max-w-xs z-[110]" overlayClassName="z-[110]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Share a Page
            </DialogTitle>
          </DialogHeader>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input type="search"
              placeholder="Search pages…"
              value={pageSearchQuery}
              onChange={e => setPageSearchQuery(e.target.value)}
              className="pl-8 h-9 text-sm"
              autoFocus
            />
          </div>
          {/* Page list */}
          <ScrollArea className="h-72">
            <div className="space-y-0.5 pr-2">
              {shareablePages
                .filter(p => !pageSearchQuery || p.label.toLowerCase().includes(pageSearchQuery.toLowerCase()))
                .map(page => (
                  <button
                    key={page.href}
                    onClick={() => handleSendSharedContent({
                      contentType: 'page',
                      title: page.label,
                      link: page.href,
                    })}
                    className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                  >
                    <span className="text-lg leading-none w-7 text-center flex-shrink-0">{page.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{page.label}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{page.href}</p>
                    </div>
                    <Share2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              {shareablePages.filter(p => !pageSearchQuery || p.label.toLowerCase().includes(pageSearchQuery.toLowerCase())).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">No pages match</p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Availability Share Dialog — pick which of your own upcoming open slots
          to post as one card. Manual only: a snapshot at share-time, not a
          live booking. Add/edit slots on the /schedule page. */}
      <Dialog open={showAvailabilityShare} onOpenChange={setShowAvailabilityShare}>
        <DialogContent className="max-w-xs z-[110]" overlayClassName="z-[110]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              {t('shareAvailabilityTitle')}
            </DialogTitle>
          </DialogHeader>
          {availabilityShareLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : availabilitySlotsToShare.length === 0 ? (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-muted-foreground">{t('shareAvailabilityEmpty')}</p>
              <Link href="/profile?section=schedule" onClick={() => setShowAvailabilityShare(false)}>
                <Button size="sm" variant="outline" className="gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {t('goToSchedule')}</Button>
              </Link>
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[45vh]">
                <div className="space-y-1 pr-2">
                  {availabilitySlotsToShare.map(s => {
                    const checked = selectedAvailabilityIds.has(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedAvailabilityIds(prev => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                          return next;
                        })}
                        className={cn(
                          "w-full flex items-start gap-2.5 p-2 rounded-lg border text-left transition-colors",
                          checked ? "border-primary/50 bg-primary/5" : "border-border hover:bg-secondary/50"
                        )}
                      >
                        <div className={cn(
                          "mt-0.5 h-4 w-4 rounded border flex items-center justify-center flex-shrink-0",
                          checked ? "bg-primary border-primary" : "border-muted-foreground/40"
                        )}>
                          {checked && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold uppercase text-muted-foreground">{tSchedule(`type_${s.type}`)}</p>
                          <p className="text-sm font-medium">
                            {new Date(s.startAt).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            {' → '}
                            {new Date(s.endAt).toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })}
                          </p>
                          {s.note && <p className="text-xs text-muted-foreground truncate">{s.note}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setShowAvailabilityShare(false)} disabled={sendingAvailability}>
                  {tCommon('cancel')}
                </Button>
                <Button size="sm" onClick={handleSendAvailability} disabled={sendingAvailability || selectedAvailabilityIds.size === 0}>
                  {sendingAvailability ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  {t('shareSelected', { count: selectedAvailabilityIds.size })}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Group name dialog — z-[110] so it appears above the chat window (z-[55]) and FAB (z-[105]) */}
      <Dialog open={groupNameDialogOpen} onOpenChange={setGroupNameDialogOpen}>
        <DialogContent className="max-w-sm z-[110]" overlayClassName="z-[110]">
          <DialogHeader>
            <DialogTitle>Name Your Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {selectedContactIds.size + 1} member{selectedContactIds.size > 0 ? 's' : ''} including you.
            </p>
            <Input
              placeholder="e.g. The Crew, Studio Session…"
              value={groupNameInput}
              onChange={e => setGroupNameInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && groupNameInput.trim() && handleCreateGroup()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupNameDialogOpen(false)} disabled={groupCreating}>
              {tCommon('cancel')}
            </Button>
            <Button onClick={handleCreateGroup} disabled={!groupNameInput.trim() || groupCreating}>
              {groupCreating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{tCommon('processing')}</> : tCommon('create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
